import isPropValid from "@emotion/is-prop-valid";
import { ThemeProvider as Emotion10ThemeProvider } from "@emotion/react";
import { HeroUIProvider } from "@heroui/react";
import {
  ThemeProvider as MuiThemeProviderV5,
  Theme,
  ThemeProvider,
} from "@mui/material/styles";
import {
  ACTIVE_PROVIDER_SEARCH_VERSIONS,
  APP_PROVIDER_SEARCH,
  NEXTUI_LOCALE,
  PROVIDER_SEARCH_THEMES,
  PROVIDER_SEARCH_VERSION_BAVARIA,
  THEME_CLASSES,
} from "core/consts";
import { getProviderSearchFromPathname } from "core/model/providerSearch";
import { isStoryshots } from "core/model/utils/featureFlags";
import { AppType } from "core/types";
import { getTheme } from "ds_legacy/materials/theme";
import React, { useCallback, useEffect, useMemo } from "react";
import {
  To,
  useHref as useReactRouterHref,
  useNavigate as useReactRouterNavigate,
  type NavigateFunction,
} from "react-router-dom";
import {
  ThemeProvider as StyledThemeProvider,
  StyleSheetManager,
} from "styled-components";
import { useLocale } from "translations";

declare module "@mui/styles/defaultTheme" {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions, @typescript-eslint/no-empty-object-type
  interface DefaultTheme extends Theme {}
}

function useAddThemeClass({ app }: { app?: AppType }) {
  const providerSearch = getProviderSearchFromPathname();

  const themeClass = useMemo<string | undefined>(() => {
    if (!app) return undefined;

    if (app === APP_PROVIDER_SEARCH) {
      const isValidProviderSearch =
        providerSearch &&
        ACTIVE_PROVIDER_SEARCH_VERSIONS.includes(providerSearch);

      if (!isValidProviderSearch) {
        if (window.IN_STORYBOOK) {
          console.log(
            "[themeClass] no provider search state found in Storybook, using Bavaria theme",
            PROVIDER_SEARCH_THEMES[PROVIDER_SEARCH_VERSION_BAVARIA],
          );
          return PROVIDER_SEARCH_THEMES[PROVIDER_SEARCH_VERSION_BAVARIA];
        } else {
          console.warn("[themeClass] no provider search state found");
          return undefined;
        }
      }

      return PROVIDER_SEARCH_THEMES[providerSearch];
    }

    return THEME_CLASSES[app];
  }, [app, providerSearch]);

  useEffect(() => {
    const targetElement = document.body;

    if (!targetElement?.classList) {
      console.warn("[useAddThemeClass] no target element found");
      return;
    }

    const allThemes = [
      ...Object.values(THEME_CLASSES),
      ...Object.values(PROVIDER_SEARCH_THEMES),
    ].filter(Boolean);

    if (allThemes.length) {
      targetElement.classList.remove(...allThemes);
    }

    if (themeClass) {
      targetElement.classList.add("overscroll-none", themeClass);
    }

    return () => {
      if (themeClass) {
        targetElement.classList.remove("overscroll-none", themeClass);
      }
    };
  }, [themeClass]);
}

const disallowedProps = new Set([
  "aligned",
  "bold",
  "borderColor",
  "backgroundColor",
  "borderHoverColor",
  "error",
  "fullWidth",
  "hovered",
  "light",
  "maxWidth",
  "noIndent",
  "primary",
  "show",
  "variant",
  "whiteSpace",
]);

function shouldForwardProp(propName: string, target: any) {
  // Always filter out disallowed props regardless of target type
  if (disallowedProps.has(propName)) {
    return false;
  }

  // For string targets (DOM elements), use isPropValid to check if it's a valid DOM prop
  if (typeof target === "string") {
    return isPropValid(propName);
  }

  // For other targets (React components), allow the prop to pass through
  return true;
}

const isToAbsolute = (to: To): boolean =>
  typeof to === "string" &&
  (to.startsWith("https://") ||
    to.startsWith("http://") ||
    to.startsWith("mailto:"));

const useHref = (to: string): string => {
  const reactRouterHref = useReactRouterHref(to);
  return isToAbsolute(to) ? to : reactRouterHref;
};

// See https://github.com/adobe/react-spectrum/issues/6397.
const useNavigate = (): NavigateFunction => {
  const reactRouterNavigate = useReactRouterNavigate();
  return useCallback(
    (p) => {
      if (typeof p === "number") return reactRouterNavigate(p);
      if (isToAbsolute(p)) {
        window.location.href = p as string;
      } else {
        return reactRouterNavigate(p);
      }
    },
    [reactRouterNavigate],
  );
};

export function NextUIThemeProvider({
  app,
  children,
}: {
  app: AppType;
  children: React.ReactNode;
}) {
  const locale = useLocale();
  useAddThemeClass({ app });
  const routerNavigate = useNavigate();

  return (
    <HeroUIProvider
      disableAnimation={isStoryshots}
      locale={NEXTUI_LOCALE[locale ?? "de"]}
      navigate={routerNavigate}
      useHref={useHref}
    >
      {children}
    </HeroUIProvider>
  );
}

export default function RecareThemeProvider({
  app,
  children,
}: {
  app: AppType;
  children: React.ReactNode;
}) {
  return (
    <Emotion10ThemeProvider theme={getTheme(app)}>
      <StyleSheetManager shouldForwardProp={shouldForwardProp}>
        <StyledThemeProvider theme={getTheme(app)}>
          <MuiThemeProviderV5 theme={getTheme(app)}>
            <ThemeProvider theme={getTheme(app)}>{children}</ThemeProvider>
          </MuiThemeProviderV5>
        </StyledThemeProvider>
      </StyleSheetManager>
    </Emotion10ThemeProvider>
  );
}
